// Frontend: EditUser.jsx
import { useTheme } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { Button, Snackbar, Alert } from "@mui/material";

import HeaderLayout from "../../../../components/HeaderLayout";
import InputTextField from "../../../../components/InputTextField";
import SearchableDropdown from "../../../../components/SearchableDropdown";
import FormikSubmitButton from "../../../../components/FormikSubmitButton";
import api from "../../../../api/apiClient";
import LoadingSpinner from "../../../../components/LoadingSpinner"; // ✅ import spinner

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  role: Yup.mixed().required("Role is required"),
});

export default function EditUser() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [loading, setLoading] = useState(true); // ✅ new state
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  const navigateBack = () => navigate(-1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesRes, userRes] = await Promise.all([
          api.get("/role/dropDownRoles"),
          api.get(`/user/${id}`),
        ]);

        const roleOptions = rolesRes.data.success
          ? rolesRes.data.data.map((r) => ({ label: r.name, value: r.code }))
          : [];

        setRoles(roleOptions);

        if (userRes.data.success) {
          const user = userRes.data.data;
          const selectedRole =
            roleOptions.find((r) => r.value === user.UserRole) || null;
          setInitialValues({
            name: user.UserName || "",
            email: user.EmailID || "",
            role: selectedRole,
          });
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
        setLoadingRoles(false);
      }
    };

    fetchData();
  }, [id]);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const payload = {
        UserName: values.name.trim(),
        EmailID: values.email.trim(),
        UserRole: values.role.value, // send the code, not label
      };

      const res = await api.put(`/user/${id}`, payload);

      if (!res.data.success) {
        throw new Error(res.data.message || "Update failed");
      }

      setToastOpen(true);

      setTimeout(() => navigate("/user-list"), 800);
    } catch (error) {
      console.error("Error updating user:", error.message);
      setFieldError("submit", error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-screen mr-1 ml-1">
      <div className="flex-shrink-0">
        <HeaderLayout name="Edit User" />
      </div>

      {loading ? ( // ✅ show spinner while loading
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner text="" />
        </div>
      ) : (
        <Formik
          initialValues={initialValues || { name: "", email: "", role: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, errors, isValid, dirty }) => (
            <Form className="flex flex-col flex-1">
              <div className="overflow-y-auto h-[62vh] px-5 py-6 space-y-10 hide-scrollbar">
                {errors.submit && (
                  <div className="bg-red-100 text-red-700 p-3 rounded-md">
                    {errors.submit}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-[100px] !pl-3 !pr-3">
                  <InputTextField
                    label="Name"
                    requiredMark
                    name="name"
                    placeholder="Enter Name"
                    type="text"
                    className="w-full"
                  />
                  <InputTextField
                    label="Email Address"
                    requiredMark
                    name="email"
                    placeholder="Enter Email Address"
                    type="email"
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-[100px] !pl-3 !pr-3">
                  <SearchableDropdown
                    label="User Role"
                    name="role"
                    required
                    options={roles} // [{label, value}]
                    placeholder="Select Role"
                    getOptionValue={(option) => option.value}
                    getOptionLabel={(option) => option.label}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="flex-shrink-0 flex items-center gap-4 justify-center pt-4 border-t bg-white">
                <Button
                  onClick={navigateBack}
                  className="w-[90px] h-[40px]"
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.buttonHover.main,
                    "&:hover": {
                      backgroundColor: theme.palette.buttonHover.secondary,
                    },
                    color: theme.palette.white.main,
                    textTransform: "none",
                  }}
                >
                  Back
                </Button>

                <FormikSubmitButton
                  name={isSubmitting ? "Updating..." : "Update"}
                  disabled={isSubmitting || !isValid || !dirty}
                  className="w-[90px] h-[40px]"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}

      {/* Success Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={2000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          User successfully updated!
        </Alert>
      </Snackbar>
    </div>
  );
}
